<template>
  <form @submit.prevent="purchase">
    <h1 class="text-primary">{{ $t(`global.checkout-payment`) }}</h1>
    <div class="form-row">
      <FormInput
        v-if="primaryCard"
        class="col-md-12"
        name="number"
        :label="`${$t('global.cc-number')} *`"
        :value="primaryCard.number"
        :disabled="true"
      />
      <FormInput
        v-else
        class="col-md-12"
        name="number"
        v-model="fields.number"
        test-id="cc-number"
        :label="`${$t('global.cc-number')} *`"
        :errors="validation.fields.number.errors"
        :dirty="validation.fields.number.dirty"
      />
    </div>
    <div class="form-row">
      <FormInput
        v-if="primaryCard"
        class="col-md-6"
        name="expiration"
        :label="`${$t('global.expiration')} *`"
        :value="formatExpiration(primaryCard.expiration)"
        :disabled="true"
      />
      <FormExpirationMonth
        v-else
        class="col-md-6"
        name="expiration"
        v-model="fields.expiration"
        test-id="expiration"
        :placeholder="'MM / YYYY'"
        :label="`${$t('global.expiration')} *`"
        :errors="validation.fields.expiration.errors"
        :dirty="validation.fields.expiration.dirty"
      />
      <FormInput
        v-if="primaryCard"
        class="col-md-6"
        name="csv"
        value="***"
        test-id="csv"
        :label="`${$t('global.csv')} *`"
        :disabled="true"
      />
      <FormInput
        v-else
        class="col-md-6"
        name="csv"
        v-model="fields.csv"
        test-id="csv"
        :label="`${$t('global.csv')} *`"
        :errors="validation.fields.csv.errors"
        :dirty="validation.fields.csv.dirty"
      />
    </div>
    <div class="text-right my-4">{{ `* ${$t("global.required")}` }}</div>

    <!-- coupon form; @verify will return a coupon code and discount amount -->
    <CouponForm :amount="packageFee" :term="term" :package-id="packageId" @verify="applyCoupon" />

    <div class="mt-4 text-right">
      <Btn class="mr-2" variant="light" @click="back" :disabled="loading">
        {{ $t("global.back") }}
      </Btn>
      <Btn variant="primary" type="submit" :loading="loading">
        {{ $t("global.purchase") }}
      </Btn>
    </div>
  </form>
</template>

<script>
import { computed, onMounted, reactive, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import CouponForm from "@/components/display/CouponForm";
import { format } from "date-fns";
import FormExpirationMonth from "@/components/display/form/FormExpirationMonth";
import FormInput from "@/components/display/form/FormInput";
import { fromServer } from "@/utils/translations";
import store from "@/store";
import useAnalytics from "@/utils/useAnalytics";
import useValidator from "@/utils/useValidator";
import { addCreditCard as validationRules } from "leatherman-js/utils/validations/forms";

export default {
  name: "CheckoutPaymentPage",
  components: {
    Btn,
    CouponForm,
    FormExpirationMonth,
    FormInput,
  },
  setup(props, context) {
    const loading = ref(true);
    const errors = ref(null);
    const client = computed(() => store.getters["auth/getClient"]);
    const term = ref(store.state.checkout.term);
    const packageId = computed(() => store.getters["checkout/getPackageId"]);
    const billingAddress = computed(() => store.getters["addresses/getBillingAddress"]);
    const requireStoreBuilder = computed(() => store.getters["checkout/requireStoreBuilder"]);

    // form fields
    const fields = reactive({
      number: null,
      expiration: null,
      csv: null,
    });

    // payemnt form validation
    const { validator } = useValidator();
    const validation = validator(
      fields,
      validationRules,
      {},
      {
        number: {
          required: context.root.$t("validation.cc-number-required"),
          regex: context.root.$t("validation.invalid-cc-number"),
        },
        expiration: {
          required: context.root.$t("validation.cc-expiration-required"),
          regex: context.root.$t("validation.invalid-cc-expiration"),
        },
        csv: {
          required: context.root.$t("validation.csv-required"),
          regex: context.root.$t("validation.invalid-csv"),
        },
      }
    );

    // go to previous checkout step
    const back = () => {
      context.root.$router.push({
        name: "checkout-billing",
        query: store.getters["checkout/getQueryParams"],
      });
    };

    const packageFee = computed(() => {
      const { appId, packageId } = store.state.checkout;
      return store.getters["checkout/getPackageFee"](
        store.getters["appPackages/getAppPackage"](packageId, appId)
      );
    });

    // check if coupon is valid and add to store
    const applyCoupon = (couponCode, discountAmount) => {
      store.dispatch("checkout/setCouponCode", couponCode);
      store.dispatch("checkout/setDiscount", discountAmount);
      store.dispatch("checkout/calculateTax");
    };

    // primary card (if one exists)
    const primaryCard = computed(() => store.getters["creditcards/primary"]);
    const formatExpiration = (timestamp) => format(timestamp * 1000, "MM/yyyy");
    const { cartData, userData, orderDataFormat } = useAnalytics();

    const createPlan = () => {
      store.dispatch("analytics/send", { name: "checkoutPurchaseInitiated", data: cartData.value });

      store
        .dispatch("checkout/createPlan")
        .then((data) => {
          store.dispatch("analytics/send", {
            name: "checkoutPurchaseCompleted",
            data: {
              cartData: { ...cartData.value },
              userData: { ...userData.value },
              orderData: orderDataFormat(
                data,
                store.state.checkout.couponCode,
                store.state.checkout.discount
              ),
            },
          });
          store.dispatch("cartstack/confirm");
          store.dispatch("analytics/send", {
            name: "navigation",
            data: {
              fullPath: "/thank-you",
              meta: { title: "Checkout Thank You" },
            },
          });
          store.dispatch("checkout/reset");

          // upon successful completion of order redirect to plan list page
          context.root.$router.push({ name: "plans" });
        })
        .catch((e) => {
          const errors = fromServer(e.request.response);
          if (Array.isArray(errors)) {
            errors.forEach((error) =>
              store.dispatch("toasts/addToast", {
                variant: "error",
                message: error,
                timeout: true,
              })
            );
          } else {
            store.dispatch("toasts/addToast", {
              variant: "error",
              message: errors,
              timeout: true,
            });
          }
        })
        .finally(() => {
          loading.value = false;
        });
    };

    // complete purchase
    const purchase = () => {
      loading.value = true;
      errors.value = false;
      validation.reset();
      // if primary creditcard already exists, update it instead of creating a new one
      if (primaryCard.value) {
        createPlan();
      } else {
        validation
          .validate()
          .then(() => {
            // if no credit card is required to checkout, skip ahead
            if (client.value && !client.value.requiresCreditCard) {
              return Promise.resolve();
            }

            // create new creditcard
            return store.dispatch("creditcards/create", fields).catch((e) => {
              const errors = fromServer(e.request.response);
              if (Array.isArray(errors)) {
                errors.forEach((error) =>
                  store.dispatch("toasts/addToast", {
                    variant: "error",
                    message: error,
                    timeout: true,
                  })
                );
              } else {
                store.dispatch("toasts/addToast", {
                  variant: "error",
                  message: errors,
                  timeout: true,
                });
              }
            });
          })
          .then(() => {
            // finish creating new plan
            createPlan();
          });
      }
    };

    const formEnabled = ref(true);

    onMounted(() => {
      if (
        !store.state.checkout.appId ||
        !store.state.checkout.packageId ||
        !store.state.checkout.locationId ||
        (!requireStoreBuilder.value && !store.state.checkout.nickname) ||
        (requireStoreBuilder.value && !store.state.checkout.storeBuilderId) ||
        !store.getters["auth/isLoggedIn"]
      ) {
        back();
      }
      // get all of the client's existing credit cards
      store
        .dispatch("creditcards/getList")
        .then(() => store.dispatch("addresses/getList"))
        .then(() => {
          if (billingAddress.value) {
            return store.dispatch("checkout/calculateTax");
          }
          return Promise.resolve();
        })
        .then(() => {
          // if primary card exists, pre-populate form, and disable inputs
          if (primaryCard.value) {
            formEnabled.value = false;
          }
        })
        .finally(() => {
          loading.value = false;
        });
    });

    return {
      back,
      applyCoupon,
      purchase,
      fields,
      term,
      packageId,
      primaryCard,
      validation,
      loading,
      formEnabled,
      formatExpiration,
      packageFee,
    };
  },
};
</script>
